import React from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import {LayersIcon,FeatherIcon,PrintIcon,LaserIcon,ArrowRightIcon,TShirtIcon,HoodieIcon,BagIcon,ScarfIcon,BusinessIcon,EventIcon} from './icons/ModernIcons';

const Services=()=> {
  const {ref,inView}=useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services=[
    {
      icon: LayersIcon,
      title: 'FLEX-Folie',
      description: 'Präzise Schnitte für einfarbige Designs mit langer Haltbarkeit',
      features: ['Langlebig','Waschbeständig','Präzise Konturen'],
      color: 'blue'
    },
    {
      icon: FeatherIcon,
      title: 'Beflockung',
      description: 'Samtiges Gefühl für hochwertige,elegante Textilveredelung',
      features: ['Samtige Oberfläche','Premium Look','Angenehm zu tragen'],
      color: 'purple'
    },
    {
      icon: PrintIcon,
      title: 'DTF-Druck',
      description: 'Vollfarb-Designs mit fotorealistischer Qualität möglich',
      features: ['Vollfarb möglich','Fotoqualität','Komplexe Motive'],
      color: 'green'
    },
    {
      icon: LaserIcon,
      title: 'Lasertransferdruck',
      description: 'Hochpräzise Laserverfahren für professionelle Textilveredelung',
      features: ['Höchste Präzision','Dauerhafte Haftung','Professionelle Optik'],
      color: 'orange'
    }
  ];

  const products=[
    {
      icon: TShirtIcon,
      name: 'T-Shirts',
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      icon: HoodieIcon,
      name: 'Hoodies',
      image: 'https://images.unsplash.com/photo-1556821840-3a9c6dcb3e1c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      icon: BagIcon,
      name: 'Taschen',
      image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      icon: ScarfIcon,
      name: 'Schals',
      image: 'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      icon: BusinessIcon,
      name: 'Firmenbekleidung',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    },
    {
      icon: EventIcon,
      name: 'Event-Textilien',
      image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80'
    }
  ];

  return (
    <section id="services" className="py-20 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid)" />
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          initial={{opacity: 0,y: 50}}
          animate={inView ? {opacity: 1,y: 0} : {}}
          transition={{duration: 0.8}}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Unsere{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Druckverfahren
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professionelle Textilveredelung mit modernsten Verfahren für beste Qualität und Langlebigkeit
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {services.map((service,index)=> (
            <motion.div
              key={service.title}
              initial={{opacity: 0,y: 50}}
              animate={inView ? {opacity: 1,y: 0} : {}}
              transition={{duration: 0.8,delay: index * 0.2}}
              whileHover={{y: -10}}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all border border-gray-100 group"
            >
              {/* Icon Header */}
              <div className={`relative h-32 bg-gradient-to-br from-${service.color}-500 to-${service.color}-600 flex items-center justify-center`}>
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-20">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <pattern id={`pattern-${service.color}`} width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="1" fill="white" opacity="0.3"/>
                      </pattern>
                    </defs>
                    <rect width="100" height="100" fill={`url(#pattern-${service.color})`} />
                  </svg>
                </div>
                
                {/* Icon */}
                <motion.div
                  whileHover={{scale: 1.2,rotate: 10}}
                  className="relative z-10 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg"
                >
                  <service.icon className="w-8 h-8 text-white" />
                </motion.div>

                {/* Floating elements */}
                <motion.div
                  animate={{y: [-10,10,-10],rotate: [0,180,360]}}
                  transition={{duration: 8,repeat: Infinity,ease: "easeInOut"}}
                  className="absolute top-4 right-4 w-4 h-4 bg-white/30 rounded-full"
                />
                <motion.div
                  animate={{y: [10,-10,10],rotate: [360,180,0]}}
                  transition={{duration: 6,repeat: Infinity,ease: "easeInOut"}}
                  className="absolute bottom-4 left-4 w-3 h-3 bg-white/20 rounded-full"
                />
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">{service.description}</p>
                
                <div className="space-y-2 mb-4">
                  {service.features.map((feature,idx)=> (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className={`w-2 h-2 bg-${service.color}-500 rounded-full`} />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <motion.div
                  whileHover={{scale: 1.05}}
                  className={`inline-flex items-center text-${service.color}-600 font-semibold hover:text-${service.color}-700 cursor-pointer`}
                >
                  <span>Mehr erfahren</span>
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{opacity: 0,y: 50}}
          animate={inView ? {opacity: 1,y: 0} : {}}
          transition={{duration: 0.8,delay: 0.8}}
          className="bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
          
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center relative z-10">
            Ideal für alle Textilien
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 relative z-10">
            {products.map((product,index)=> (
              <motion.div
                key={product.name}
                whileHover={{scale: 1.1}}
                className="text-center group"
              >
                <div className="relative w-16 h-16 mx-auto mb-3 rounded-xl overflow-hidden group-hover:shadow-lg transition-all">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent group-hover:from-blue-500/30 transition-all"></div>
                  <product.icon className="absolute inset-0 w-6 h-6 text-white m-auto" />
                </div>
                <p className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                  {product.name}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{opacity: 0,y: 50}}
          animate={inView ? {opacity: 1,y: 0} : {}}
          transition={{duration: 0.8,delay: 1}}
          className="text-center mt-12"
        >
          <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="Pattern"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10">
              <h3 className="text-3xl font-bold mb-4">Ab 3,00 € pro Druck</h3>
              <p className="text-blue-100 mb-6">
                Faire Preise für höchste Qualität - egal ob Einzelstück oder Großauftrag
              </p>
              <motion.a
                href="#contact"
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                className="inline-flex items-center space-x-2 bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                <span>Angebot anfordern</span>
                <ArrowRightIcon className="w-5 h-5" />
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;